import { useState, type ReactNode } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { exportRegistrations, exportResults, finaliseResults, getCampaignStats, getCampaigns, setReportVisibility, updateCampaign } from '../api/endpoints'
import type { CampaignRow, CampaignSettings } from '../api/types'
import { ApiError } from '../api/client'
import { ErrorNotice, ReasonDialog } from '../components'
import { fromLocalInput, linkState, toLocalInput } from '../lib/format'
import QuestionsEditor from './QuestionsEditor'
import TestPlanEditor from './TestPlanEditor'
import CountUp from '../CountUp'
import { Notice, useToast } from '../Toast'
import HowItWorks from '../HowItWorks'

const CANDIDATE_APP = (import.meta.env.VITE_CANDIDATE_APP_URL || (import.meta.env.DEV ? 'http://localhost:5173' : window.location.origin)).replace(/\/$/, '')
// The server knows where the candidate app lives (CANDIDATE_APP_URL); the build setting and this site's own address are only fallbacks.
const registrationLink = (c: Pick<CampaignRow, 'institutionSlug' | 'slug' | 'candidateAppUrl'>) => `${c.candidateAppUrl || CANDIDATE_APP}/register/${c.institutionSlug}/${c.slug}`

type Tab = 'overview' | 'registration' | 'form' | 'tests'
const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' }, { id: 'registration', label: 'Registration' }, { id: 'form', label: 'Form questions' }, { id: 'tests', label: 'Tests and results' },
]

/** Every setting of a campaign as the server wants it, so one change can be saved without disturbing the others. */
const settingsOf = (c: CampaignRow, patch: Partial<CampaignSettings> = {}): CampaignSettings => ({
  title: c.title, welcomeText: c.welcomeText, slug: c.slug, registrationOpen: c.registrationOpen, opensAt: c.opensAt, closesAt: c.closesAt, registrationLimit: c.registrationLimit,
  programRequired: c.programRequired, guardianConsentRequired: c.guardianConsentRequired, verifyEmail: c.verifyEmail, ipLimitPerHour: c.ipLimitPerHour, description: c.description,
  emailReportToCandidate: c.emailReportToCandidate, ...patch,
})

export default function CampaignDetailPage() {
  const id = Number(useParams().id)
  const [params, setParams] = useSearchParams()
  const campaigns = useQuery({ queryKey: ['campaigns'], queryFn: getCampaigns })
  const c = campaigns.data?.find((x) => x.id === id)
  if (campaigns.isPending) return <p role="status">Loading…</p>
  if (!c) return <div><p><Link to="/campaigns">← Campaigns</Link></p><ErrorNotice error={campaigns.error} />{!campaigns.error && <p>Campaign not found.</p>}</div>
  const tab: Tab = TABS.some((t) => t.id === params.get('tab')) ? (params.get('tab') as Tab) : 'overview'
  const go = (t: Tab) => setParams(t === 'overview' ? {} : { tab: t }, { replace: true })
  return (
    <div className="campaign">
      <p style={{ margin: 0 }}><Link to="/campaigns">← Campaigns</Link></p>
      <h1>{c.title}</h1>
      <LinkCard c={c} />
      <div className="tabs" role="tablist" aria-label="Campaign sections">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" id={`tab-${t.id}`} aria-selected={tab === t.id} aria-controls="tab-panel" className={`tab${tab === t.id ? ' on' : ''}`} onClick={() => go(t.id)}>{t.label}</button>
        ))}
      </div>
      <div id="tab-panel" role="tabpanel" aria-labelledby={`tab-${tab}`} className="tab-panel">
        {tab === 'overview' && <Overview c={c} go={go} />}
        {tab === 'registration' && <RegistrationForm key={`${c.id}-${c.opensAt}-${c.closesAt}-${c.registrationLimit}-${c.registrationOpen}-${c.programRequired}-${c.guardianConsentRequired}-${c.verifyEmail}-${c.ipLimitPerHour}-${c.slug}-${c.title}`} c={c} />}
        {tab === 'form' && <QuestionsEditor campaignId={c.id} />}
        {tab === 'tests' && <><TestPlanEditor campaignId={c.id} programRequired={c.programRequired} /><ResultsPanel c={c} /><Downloads c={c} /></>}
      </div>
    </div>
  )
}

/** The link and exactly when it stops working: always visible, above the tabs. */
function LinkCard({ c }: { c: CampaignRow }) {
  const [copied, setCopied] = useState(false)
  const toast = useToast()
  const s = linkState(c)
  const link = registrationLink(c)
  const headline = s.state === 'OPEN' ? 'Open' : s.state === 'OFF' ? 'Off' : s.state === 'SCHEDULED' ? 'Not open yet' : s.state === 'FULL' ? 'Full' : 'Expired'
  const tone = s.state === 'OPEN' ? 'ok' : s.state === 'OFF' || s.state === 'SCHEDULED' ? 'warn' : 'bad'
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      toast('Link copied.', 'ok')
      setTimeout(() => setCopied(false), 2500)
    } catch {
      window.prompt('Copy this link', link)
    }
  }
  return (
    <section className="card link-card" aria-label="Registration link">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span><span className={`badge ${tone}`}>{headline}</span> <span className="muted">{s.message}</span></span>
      </div>
      <div className="row"><code data-testid="reg-link" style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{link}</code>
        <button type="button" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy link'}</button>
        <a className="btn" href={link} target="_blank" rel="noopener noreferrer">Preview</a></div>
    </section>
  )
}

/** What is still to do before the link goes out, each with a way to the right tab. */
function Overview({ c, go }: { c: CampaignRow; go: (t: Tab) => void }) {
  const plan = c.hasGeneralPlan || c.programCount > 0
  const items: { done: boolean; text: ReactNode; tab: Tab }[] = [
    { done: plan, text: 'Choose the tests candidates take', tab: 'tests' },
    { done: c.fieldCount > 0, text: 'Decide what the registration form asks', tab: 'form' },
    { done: !!c.closesAt, text: 'Set a closing date, so the link stops on its own', tab: 'registration' },
    { done: c.registrationOpen, text: 'Switch registration on', tab: 'registration' },
  ]
  const remaining = items.filter((i) => !i.done)
  return (
    <>
      <HowItWorks id="campaign" title="How a campaign works" steps={[
        { icon: 'edit', title: 'Registration', text: 'Name the page candidates see, set when it opens and closes, and who may register.' },
        { icon: 'content', title: 'Form questions', text: 'Choose what else to ask, such as school, grade or a parent\'s details.' },
        { icon: 'schedule', title: 'Tests and results', text: 'Pick the tests each program takes, then control reports and results once testing is over.' },
        { icon: 'check', title: 'Share and watch', text: 'Send the link above. This page shows who has registered, started and finished.' },
      ]} />
      <section className="card" aria-labelledby="todo-title">
        <h2 id="todo-title">{remaining.length === 0 ? 'Ready to go' : `Before you open this campaign (${remaining.length} to do)`}</h2>
        <ul className="checklist">
          {items.map((i) => (
            <li key={i.tab + String(i.text)} className={i.done ? 'done' : ''}>
              <span className="tick" aria-hidden="true">{i.done ? '✓' : '○'}</span>
              <span>{i.text}{i.done && <span className="sr-only"> (done)</span>}</span>
              {!i.done && <button type="button" onClick={() => go(i.tab)}>Go</button>}
            </li>
          ))}
        </ul>
      </section>
      <Funnel id={c.id} />
    </>
  )
}

function Funnel({ id }: { id: number }) {
  const stats = useQuery({ queryKey: ['campaign-stats', id], queryFn: () => getCampaignStats(id), refetchInterval: 30_000 })
  if (!stats.data) return <ErrorNotice error={stats.error} />
  const f = stats.data.funnel
  const days = stats.data.registrationsByDay
  const max = Math.max(1, ...days.map((d) => d.count))
  const pct = (n: number) => (f.registered === 0 ? '' : ` (${Math.round((n / f.registered) * 100)}%)`)
  return (
    <section className="card" aria-labelledby="funnel-title">
      <h2 id="funnel-title">How it is going</h2>
      <div className="grid">
        <div className="stat"><b><CountUp value={f.registered} /></b><span>Registered ({f.selfRegistered} via the link, {f.invited} invited)</span></div>
        <div className="stat"><b><CountUp value={f.emailVerified} /></b><span>Confirmed their email{pct(f.emailVerified)}</span></div>
        <div className="stat"><b><CountUp value={f.consented} /></b><span>Gave consent{pct(f.consented)}</span></div>
        <div className="stat"><b><CountUp value={f.started} /></b><span>Started a test{pct(f.started)}</span></div>
        <div className="stat"><b><CountUp value={f.completed} /></b><span>Finished both tests{pct(f.completed)}</span></div>
        <div className="stat"><b><CountUp value={f.scored} /></b><span>Scored{pct(f.scored)}</span></div>
      </div>
      {days.length > 0 && (
        <>
          <h3>Registrations per day (last 30 days)</h3>
          <div role="img" aria-label={`Registrations per day: ${days.map((d) => `${d.date}: ${d.count}`).join(', ')}`} style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80 }}>
            {days.map((d) => <div key={d.date} title={`${d.date}: ${d.count}`} style={{ flex: 1, maxWidth: 24, height: `${(d.count / max) * 100}%`, minHeight: 3, background: 'var(--brand)', borderRadius: 3 }} />)}
          </div>
        </>
      )}
      {stats.data.byProgram.length > 1 && <p className="muted">{stats.data.byProgram.map((p) => `${p.program}: ${p.count}`).join(' · ')}</p>}
    </section>
  )
}

/** A checkbox with a one-line reason and the longer explanation tucked away. */
function Choice({ checked, onChange, label, short, more, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; short: string; more?: string; disabled?: boolean }) {
  return (
    <div className="choice">
      <label className="row"><input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} /> <b>{label}</b></label>
      <p className="muted">{short}</p>
      {more && <details><summary>More about this</summary><p className="muted">{more}</p></details>}
    </div>
  )
}

/** Registration settings. Nothing is saved until Save is pressed, and the bar at the bottom says when there is something to save. */
function RegistrationForm({ c }: { c: CampaignRow }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [resets, setResets] = useState(0)
  return <RegistrationFields key={resets} c={c} onReset={() => setResets((n) => n + 1)} queryClient={queryClient} toast={toast} />
}

function RegistrationFields({ c, onReset, queryClient, toast }: { c: CampaignRow; onReset: () => void; queryClient: ReturnType<typeof useQueryClient>; toast: ReturnType<typeof useToast> }) {
  const [title, setTitle] = useState(c.title)
  const [welcome, setWelcome] = useState(c.welcomeText ?? '')
  const [description, setDescription] = useState(c.description ?? '')
  const [slug, setSlug] = useState(c.slug)
  const [open, setOpen] = useState(c.registrationOpen)
  const [opens, setOpens] = useState(toLocalInput(c.opensAt))
  const [closes, setCloses] = useState(toLocalInput(c.closesAt))
  const [limit, setLimit] = useState(c.registrationLimit ? String(c.registrationLimit) : '')
  const [programRequired, setProgramRequired] = useState(c.programRequired)
  const [guardianRequired, setGuardianRequired] = useState(c.guardianConsentRequired)
  const [verifyEmail, setVerifyEmail] = useState(c.verifyEmail)
  const [ipLimit, setIpLimit] = useState(String(c.ipLimitPerHour))
  const patch: Partial<CampaignSettings> = {
    title, welcomeText: welcome || null, description: description || null, slug, registrationOpen: open, opensAt: fromLocalInput(opens), closesAt: fromLocalInput(closes),
    registrationLimit: limit ? Number(limit) : null, programRequired, guardianConsentRequired: guardianRequired, verifyEmail, ipLimitPerHour: ipLimit === '' ? 0 : Number(ipLimit),
  }
  const save = useMutation({
    mutationFn: () => updateCampaign(c.id, settingsOf(c, patch)),
    onSuccess: async () => { toast('Saved.', 'ok'); await queryClient.invalidateQueries({ queryKey: ['campaigns'] }) },
  })
  const [now] = useState(() => Date.now())
  const past = closes && new Date(closes).getTime() < now
  const dirty = title !== c.title || welcome !== (c.welcomeText ?? '') || description !== (c.description ?? '') || slug !== c.slug || open !== c.registrationOpen
    || opens !== toLocalInput(c.opensAt) || closes !== toLocalInput(c.closesAt) || limit !== (c.registrationLimit ? String(c.registrationLimit) : '') || programRequired !== c.programRequired
    || guardianRequired !== c.guardianConsentRequired || verifyEmail !== c.verifyEmail || ipLimit !== String(c.ipLimitPerHour)
  return (
    <form onSubmit={(e) => { e.preventDefault(); save.mutate() }} aria-label="Registration settings">
      <section className="card">
        <h2>What candidates see</h2>
        <label className="field">Title<input value={title} onChange={(e) => setTitle(e.target.value)} /></label>
        <label className="field">Welcome message (optional)<textarea value={welcome} onChange={(e) => setWelcome(e.target.value)} rows={3} /></label>
        <label className="field">Link name<input value={slug} onChange={(e) => setSlug(e.target.value)} />
          <span className="hint">Part of the link. Changing it breaks links you have already shared.</span></label>
      </section>

      <section className="card">
        <h2>When registration is open</h2>
        <Choice checked={open} onChange={setOpen} label="Registration is open" short="Off: the link shows a “not open” message." />
        <div className="toolbar">
          <label>Opens (optional)<input type="datetime-local" value={opens} onChange={(e) => setOpens(e.target.value)} /></label>
          <label>Closes<input type="datetime-local" value={closes} onChange={(e) => setCloses(e.target.value)} /></label>
          <label>Limit<input type="number" min={1} value={limit} onChange={(e) => setLimit(e.target.value)} style={{ width: 110 }} placeholder="no limit" /></label>
        </div>
        <p className="hint">The link stops working at the closing time, or when the limit is reached.</p>
        {open && !closes && <Notice tone="info">No closing date: the link will accept registrations until you switch it off. Consider setting one.</Notice>}
        {past && <Notice tone="error">That closing time has already passed, so nobody can register.</Notice>}
      </section>

      <section className="card">
        <h2>Who can register, and how</h2>
        <Choice checked={programRequired} onChange={setProgramRequired} label="Candidates must choose a program" short="Off: the program question is optional, and anyone who skips it follows the general test plan." />
        <Choice checked={guardianRequired} onChange={setGuardianRequired} label="Candidates under 18 need a parent or guardian to consent" short="Recommended."
          more="If you switch this off, minors agree for themselves and no guardian details are asked. Whether that is acceptable for your institution is a question for your Legal team." />
        <Choice checked={verifyEmail} onChange={setVerifyEmail} label="Candidates confirm their email with a code" short="Recommended. Turn it off only for a supervised session, such as a campus hall."
          more="Off: a new candidate goes straight to the test after registering, with no code. Anyone with the link could then register with someone else's email address and take the test as them, and the email on file stays unconfirmed. An email that is already registered still gets a code, so nobody can sign in as an existing candidate." />
        <label className="field">Registrations allowed per internet connection per hour
          <input type="number" min={0} value={ipLimit} onChange={(e) => setIpLimit(e.target.value)} style={{ width: 120 }} />
          <span className="hint">Stops a script flooding the form. 0 means no limit.</span></label>
        <details><summary>Registering a whole hall on one Wi-Fi?</summary>
          <p className="muted">Everyone on a campus Wi-Fi shares one connection. For a supervised session, set this above the number you expect (for example 1000 for a hall of 500), or 0. The default of 200 would turn some of them away.</p></details>
      </section>

      <section className="card">
        <h2>Internal notes</h2>
        <label className="field">Description (staff only)
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={4000} rows={3} placeholder="e.g. source, owner, purpose of this campaign" />
          <span className="hint">Candidates never see this.</span></label>
      </section>

      <ErrorNotice error={save.error instanceof ApiError ? save.error : null} />
      <div className="savebar" role="region" aria-label="Save changes">
        <span className={dirty ? 'dirty' : 'muted'} role="status">{dirty ? 'You have unsaved changes.' : 'All changes saved.'}</span>
        <div className="row">
          <button type="button" disabled={!dirty || save.isPending} onClick={onReset}>Discard</button>
          <button type="submit" className="primary" disabled={!dirty || save.isPending}>{save.isPending ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </form>
  )
}

/** After testing: who sees results, the emailed report, and making results final. Each switch saves straight away. */
function ResultsPanel({ c }: { c: CampaignRow }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const refresh = async () => { toast('Saved.', 'ok'); await queryClient.invalidateQueries({ queryKey: ['campaigns'] }) }
  const email = useMutation({ mutationFn: (v: boolean) => updateCampaign(c.id, settingsOf(c, { emailReportToCandidate: v })), onSuccess: refresh })
  const show = useMutation({ mutationFn: (v: boolean) => setReportVisibility(c.id, v), onSuccess: refresh })
  return (
    <section className="card" aria-labelledby="res-title">
      <h2 id="res-title">Results and reports</h2>
      <p className="muted" style={{ marginTop: 0 }}>These switches save as soon as you change them.</p>
      <Choice checked={c.resultsVisibleToCandidates} disabled={show.isPending} onChange={(v) => show.mutate(v)} label="Show candidates their own results"
        short="Candidates see their scores at the end of the test and can download their report. They never see the admission band or panel notes."
        more="Takes effect straight away, and you can turn it off again." />
      <Choice checked={c.emailReportToCandidate} disabled={email.isPending} onChange={(v) => email.mutate(v)} label="Email each candidate their report as a PDF"
        short="Sent once, to the address they registered with, when their results are final."
        more="Provisional results are never emailed. Candidates who are withdrawn or disqualified do not receive it. Switching this on also sends to everyone already scored whose results are final. This is separate from showing results in the candidate app." />
      <ErrorNotice error={email.error ?? show.error} />
      <FinaliseResults c={c} />
    </section>
  )
}

function Downloads({ c }: { c: CampaignRow }) {
  const reg = useMutation({ mutationFn: (f: 'xlsx' | 'csv') => exportRegistrations(f, { cycleId: c.id }) })
  const res = useMutation({ mutationFn: (f: 'xlsx' | 'csv') => exportResults(f, { cycleId: c.id }) })
  return (
    <section className="card" aria-labelledby="dl-title">
      <h2 id="dl-title">Downloads</h2>
      <p className="muted">Both include every answer to the form questions, plus mobile number, how they registered and whether their email is confirmed.</p>
      <div className="row">
        <span><b>Registrations</b> (everyone):</span>
        <button type="button" onClick={() => reg.mutate('xlsx')} disabled={reg.isPending}>Excel</button><button type="button" onClick={() => reg.mutate('csv')} disabled={reg.isPending}>CSV</button>
        <span style={{ marginLeft: 16 }}><b>Results</b> (scored):</span>
        <button type="button" onClick={() => res.mutate('xlsx')} disabled={res.isPending}>Excel</button><button type="button" onClick={() => res.mutate('csv')} disabled={res.isPending}>CSV</button>
      </div>
      <ErrorNotice error={reg.error ?? res.error} />
    </section>
  )
}

/** Makes results final now, so final reports (and the candidate emails) go out without waiting for a closing time that was never set. */
function FinaliseResults({ c }: { c: CampaignRow }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const run = useMutation({
    mutationFn: (reason: string) => finaliseResults(c.id, reason),
    onSuccess: (r) => {
      setOpen(false)
      toast(r.cohorts > 0 ? `Results are final. Reports are being regenerated${c.emailReportToCandidate ? ' and emailed' : ''}.` : 'Nothing needed finalising: there are no finished aptitude attempts yet, or results were already final.', 'ok')
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })
  return (
    <div className="choice">
      <button type="button" onClick={() => setOpen(true)}>Finalise results now</button>
      <p className="muted">Results become final on their own once every test window has closed. If you did not set closing times, use this when testing is over: it removes “Provisional” and sends the final reports.</p>
      {open && (
        <ReasonDialog title="Finalise results for this campaign?" confirmLabel="Finalise results" busy={run.isPending} error={run.error} onCancel={() => setOpen(false)} onConfirm={(r) => run.mutate(r)}
          description="Percentiles are fixed using everyone who has finished so far, and final reports are generated. Candidates who finish later are not ranked in these percentiles. Do this only when testing is over." />
      )}
    </div>
  )
}
